import { useEffect, useState } from "react";
import { useLang } from "@/i18n";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Check, Loader2, Gem, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function PricingPage() {
  const { t, lang } = useLang();
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [trial, setTrial] = useState(null);
  const [yearly, setYearly] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  useEffect(() => {
    api.get("/payments/plans")
      .then((r) => { setPlans(r.data.plans); setCurrentPlan(r.data.current_plan); setTrial(r.data.trial); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const subscribe = async (lookupKey) => {
    setBusyKey(lookupKey);
    try {
      const r = await api.post("/payments/checkout", {
        lookup_key: lookupKey, origin_url: window.location.origin,
      });
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e));
      setBusyKey(null);
    }
  };

  return (
    <div className="max-w-5xl mx-auto space-y-6 md:pl-14" data-testid="pricing-page">
      <div className="text-center space-y-2">
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold">{t("pay.title")}</h1>
        <p className="text-sm text-muted-foreground max-w-lg mx-auto">{t("pay.subtitle")}</p>
      </div>

      {trial && !currentPlan && (
        <div data-testid="trial-banner"
          className={`max-w-2xl mx-auto rounded-2xl border-2 p-4 text-center space-y-1 ${trial.active ? "bg-emerald-50 border-emerald-300" : "bg-amber-50 border-amber-300"}`}>
          <p className={`font-display font-extrabold text-lg ${trial.active ? "text-emerald-800" : "text-amber-800"}`}>
            {trial.active ? `${t("pay.trialActive")} — ${trial.days_left} ${t("pay.trialDaysLeft")}` : t("pay.trialExpired")}
          </p>
          <p className={`text-xs ${trial.active ? "text-emerald-700" : "text-amber-700"}`}>
            {trial.active ? `${t("pay.trialBannerBody")} ${t("pay.trialFree")}` : t("pay.trialExpiredBody")}
          </p>
        </div>
      )}

      <div className="flex items-center justify-center gap-1 p-1 bg-secondary rounded-xl w-fit mx-auto" data-testid="billing-toggle">
        <button data-testid="billing-monthly" onClick={() => setYearly(false)}
          className={`h-10 px-5 rounded-lg text-sm font-semibold ${!yearly ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
          {t("pay.monthly")}
        </button>
        <button data-testid="billing-yearly" onClick={() => setYearly(true)}
          className={`h-10 px-5 rounded-lg text-sm font-semibold flex items-center gap-2 ${yearly ? "bg-card shadow-sm" : "text-muted-foreground"}`}>
          {t("pay.yearly")}
          <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">{t("pay.saveYearly")}</span>
        </button>
      </div>

      <div className="grid md:grid-cols-3 gap-4" data-testid="plans-grid">
        {plans.map((p) => {
          const lookupKey = yearly ? p.yearly_key : p.monthly_key;
          const price = yearly ? p.yearly : p.monthly;
          const isCurrent = currentPlan === p.monthly_key || currentPlan === p.yearly_key;
          const isPopular = p.plan_id === "pro";
          return (
            <div key={p.plan_id} data-testid={`plan-card-${p.plan_id}`}
              className={`bg-card rounded-2xl border-2 p-6 flex flex-col gap-5 relative ${isPopular ? "border-primary shadow-lg" : "border-border"}`}>
              {isPopular && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase px-3 py-1 rounded-full bg-primary text-white">
                  {t("pay.popular")}
                </span>
              )}
              <div className="flex items-center gap-3">
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${isPopular ? "bg-primary" : "bg-secondary"}`}>
                  <Gem className={`h-5 w-5 ${isPopular ? "text-white" : "text-muted-foreground"}`} />
                </div>
                <h2 className="font-display font-extrabold text-xl">{p.name}</h2>
              </div>
              <div>
                <span className="font-display text-4xl font-extrabold">€{price}</span>
                <span className="text-sm text-muted-foreground">{yearly ? t("pay.perYear") : t("pay.perMonth")}</span>
              </div>
              <ul className="space-y-2 flex-1">
                {(lang === "it" ? p.features_it : p.features_en).map((f, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <Check className="h-4 w-4 text-emerald-600 shrink-0" />{f}
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <div data-testid={`current-plan-${p.plan_id}`}
                  className="h-12 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-800 font-bold text-sm flex items-center justify-center gap-2">
                  <ShieldCheck className="h-4 w-4" />{t("pay.currentPlan")}
                </div>
              ) : (
                <Button data-testid={`subscribe-${p.plan_id}`} onClick={() => subscribe(lookupKey)}
                  disabled={busyKey !== null}
                  className={`h-12 tactile-btn font-bold ${isPopular ? "" : "bg-slate-900 hover:bg-slate-800"}`}>
                  {busyKey === lookupKey ? <Loader2 className="h-5 w-5 animate-spin" /> : t("pay.subscribe")}
                </Button>
              )}
            </div>
          );
        })}
      </div>

      <p className="text-center text-xs text-muted-foreground" data-testid="stripe-test-note">{t("pay.testNote")}</p>
    </div>
  );
}
